import { useEffect, useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import { useUIStore } from '../../store/uiStore'
import { getCurrencyPreference, formatCurrency } from '../../utils/currency'
import './PaymentPages.css'

type PendingCoinbaseCheckout = {
    createdAt?: number
    hostedUrl?: string
    orderId?: string
    chargeCode?: string
    chargeId?: string
    packageName?: string
    usdtAmount?: number
    deliveryNetwork?: string
    deliveryAddress?: string
}

export default function PaymentContinue() {
    const { setCurrentBoard } = useUIStore()
    const PENDING_KEY = 'flashusdt:pending_coinbase_checkout_v1'
    const [pending, setPending] = useState<PendingCoinbaseCheckout | null>(null)
    const [copied, setCopied] = useState(false)

    const hasPending = !!pending?.hostedUrl

    useEffect(() => {
        try {
            const raw = localStorage.getItem(PENDING_KEY)
            if (raw) setPending(JSON.parse(raw))
        } catch {
            // ignore
        }
    }, [])

    const ageMinutes = useMemo(() => {
        if (!pending?.createdAt) return null
        const mins = Math.floor((Date.now() - pending.createdAt) / 60000)
        return Number.isFinite(mins) ? Math.max(0, mins) : null
    }, [pending?.createdAt])

    const currencyInfo = useMemo(() => getCurrencyPreference(), [])
    
    const paymentAmount = useMemo(() => {
        if (typeof pending?.usdtAmount === 'number') {
            // Payment amount is 10% of the package amount
            return Math.round(pending.usdtAmount * 0.1)
        }
        return null
    }, [pending?.usdtAmount])

    return (
        <div className="payment-page">
            <motion.div
                className="payment-card glass"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
            >
                <h2 className="payment-title">Continue your payment</h2>
                <p className="payment-subtitle">
                    Coinbase Commerce will handle the payment. After you connect a wallet (especially via WalletConnect),
                    you may need to switch back to <strong>Chrome</strong> to finish the payment step.
                </p>

                {hasPending ? (
                    <>
                        {paymentAmount !== null && (
                            <div className="payment-amount-simple">
                                <div className="payment-amount-label">Amount to Pay</div>
                                <div className="payment-amount-value">
                                    {formatCurrency(paymentAmount, currencyInfo)}
                                </div>
                            </div>
                        )}

                        <div className="order-details-card" style={{ marginTop: 8 }}>
                            <div className="detail-row">
                                <span>Order</span>
                                <span className="text-white">{pending?.packageName || 'Coinbase Commerce'}</span>
                            </div>
                            <div className="detail-row">
                                <span>Amount</span>
                                <span className="text-neon">
                                    {typeof pending?.usdtAmount === 'number' ? `${pending.usdtAmount.toLocaleString()} USDT` : '—'}
                                </span>
                            </div>
                            <div className="detail-row">
                                <span>Delivery Network</span>
                                <span>{pending?.deliveryNetwork || '—'}</span>
                            </div>
                            <div className="detail-row">
                                <span>Created</span>
                                <span>{ageMinutes === null ? '—' : `${ageMinutes} min ago`}</span>
                            </div>
                        </div>

                        <div className="payment-actions">
                            <button
                                className="btn btn-primary"
                                onClick={() => {
                                    if (!pending?.hostedUrl) return
                                    window.location.href = pending.hostedUrl
                                }}
                            >
                                Open Coinbase Checkout
                            </button>
                            <button
                                className="btn btn-secondary"
                                onClick={async () => {
                                    if (!pending?.hostedUrl) return
                                    try {
                                        await navigator.clipboard.writeText(pending.hostedUrl)
                                        setCopied(true)
                                        setTimeout(() => setCopied(false), 1500)
                                    } catch {
                                        // ignore
                                    }
                                }}
                            >
                                {copied ? 'Copied' : 'Copy link'}
                            </button>
                        </div>

                        <div className="payment-actions payment-actions--compact" style={{ marginTop: 12 }}>
                            <button
                                className="btn btn-secondary"
                                onClick={() => window.location.href = '/payment/return'}
                            >
                                I already paid (check status)
                            </button>
                            <button
                                className="btn btn-secondary"
                                onClick={() => window.open('mailto:support@flashusdt.com')}
                            >
                                Support
                            </button>
                        </div>

                        <div className="payment-callout">
                            <strong>If WalletConnect gets “stuck”</strong>
                            <ul className="payment-list">
                                <li>Approve the connection in your wallet app.</li>
                                <li>Manually switch back to <strong>Chrome</strong>.</li>
                                <li>Tap <strong>Open Coinbase Checkout</strong> again to continue the payment step.</li>
                            </ul>
                        </div>

                        <div className="payment-actions payment-actions--compact">
                            <button
                                className="btn btn-secondary"
                                onClick={() => {
                                    try { localStorage.removeItem(PENDING_KEY) } catch { /* ignore */ }
                                    setPending(null)
                                }}
                            >
                                Clear pending payment
                            </button>
                        </div>
                    </>
                ) : (
                    <>
                        <p className="payment-subtitle" style={{ marginTop: 12 }}>
                            No pending payment was found on this device. If you already paid, use the status page to recover using a charge code.
                        </p>
                        <div className="payment-actions payment-actions--compact">
                            <button className="btn btn-primary" onClick={() => window.location.href = '/payment/return'}>
                                Go to status page
                            </button>
                            <button
                                className="btn btn-secondary"
                                onClick={() => {
                                    setCurrentBoard('ORDER')
                                    window.history.replaceState({}, '', '/')
                                }}
                            >
                                Back to checkout
                            </button>
                        </div>
                    </>
                )}
            </motion.div>
        </div>
    )
}


